/* eslint-disable react/button-has-type */
/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */
import {
  Box,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  ModalContainer,
  ShareWithOusterModal,
  Shortcut,
  ShortcutAddonKey,
  ShortcutCategory,
  ShortcutKey,
  ShortcutsModal,
  UnshareWithOusterAlert,
  downloadFileViaAnchor,
  fileExtension,
  formatFileSizeAbbreviated,
  getSafeFilename,
  processAndDownloadTar,
  useDisclosure,
  VizTitle,
  AccessLevel,
} from '@ouster/webviz-react-components';

import { Done, ArrowDownward } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { isMacOs } from 'react-device-detect';
import { TbEyeStar } from 'react-icons/tb';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components/macro';

import { SVGIcon } from './SVGIconButton';
import {
  NavBar,
  NavFullHeightContainer,
  NavFlexRow,
} from '../containers/NavBar';
import useChakraHook from '../hooks/useChakraHook';
import { useStoreState, useStoreActions } from '../reducers';
import {
  FileInfo,
  OutputFileInfo,
  RecordingPublicityAccessConfig,
} from '../types';
import { BASE_ORG_API, BASE_PUBLISHED_RECORDING_API } from '../utils/constants';
import { useFeedbackForm, useOutsideCallback } from '../utils/customHooks';
import { fetchWithAuth } from '../utils/requests';
import NavPublish from './navBar/NavPublish';
import { PublicDownloadWarning } from './publicSample/PublicDownloadWarning';
import NavUser from './users/NavUser';
import { driveSensorInfoArrayToRaw } from '../utils/driveUtil';
import RecordingAccessConfig from './recordingTable/RecordingAccessConfig';

const ACTIONS = [
  'DOWNLOAD-RECORDING',
  'SHARE-RECORDING',
  'UNSHARE-RECORDING',
  'DELETE-RECORDING',
  'PUBLISH-RECORDING',
  'UNPUBLISH-RECORDING',
  'COPY-RECORDING-LINK',
  'SAMPLE-CONFIGURE',
  'SEND-FEEDBACK',
  'KEYBOARD-SHORTCUTS',
] as const;

type TMenuItem = {
  label: string;
  action: typeof ACTIONS[number];
  icon: React.ReactElement;
};

/// ///////////////////////////////////////////////////////////////////////////
// Download Menu Components
/// ///////////////////////////////////////////////////////////////////////////

const DownloadPopup = styled.div<{ revealed: boolean }>`
  position: absolute;
  z-index: var(--z-overlay);
  padding: 16px;
  border-radius: 4px;
  background-color: var(--color-galaxy-gray);
  display: flex;
  visibility: ${(props) => (props.revealed ? 'visible' : 'hidden')};
  opacity: ${(props) => (props.revealed ? 1 : 0)};
  transition: opacity 0.15s ease-in-out;
  gap: 24px;
  font-size: 14px;
  max-height: 300px;
  overflow: auto;
`;

const DownloadColumnTitle = styled.div`
  text-transform: uppercase;
  font-weight: 500;

  /* Span is used to highlight BETA features in yellow */
  & > span {
    color: var(--color-nebula-yellow);
  }
`;

const DownloadOption = styled.button`
  /* This is text here, so the button shouldn't be styled */
  background-color: transparent;
  border: 0;
  padding: 0;
  color: var(--color-crater-gray);

  &:hover:not(:disabled) {
    cursor: pointer;
  }

  /* Column with type of download at the top, and file size at the
  bottom */
  display: flex;
  flex-direction: column;
  align-items: center;

  /* Type of download should be in a row next to a download icon */
  & > div {
    display: flex;
    gap: 2px;
  }
`;

const DownloadGrid = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  gap: 0.5rem;
`;

enum DownloadState {
  Ready = 'READY',
  // Since we rely on an <a> HTML element to trigger the download, we
  // hand the download over to the browser and we have no easy way to
  // know whether the file is still downloading or fully downloaded. The
  // only thing we know is whether the download was triggered or not,
  // and since there is a bit of delay between the click and the
  // download start, we choose here to give feedback to the user that
  // the download has started, and prevent them from clicking again.
  Triggered = 'TRIGGERED',
  // After some amount of time, we assume that the browser started (and
  // maybe even finished) the download. In this state, we will allow the
  // user to re-download their file again.
  Started = 'STARTED',
}

// The time we assume the browser will take to kick off the file
// download. During this time we will prevent the user from clicking on
// the download button again to avoid accidental double downloads.
const DOWNLOAD_KICKOFF_TIME_MS = 5e3;

type OutputType = 'input' | 'output';

const DownloadFile: React.FC<{
  name: string;
  fileInfo: FileInfo;
  type: OutputType;
  driveIdFromList?: string;
  isPublic?: boolean;
  driveOrgId?: number;
  recordingTitle?: string;
}> = ({
  name,
  fileInfo,
  type,
  driveIdFromList,
  isPublic,
  driveOrgId,
  recordingTitle,
}) => {
  const [downloadState, setDownloadState] = useState(DownloadState.Ready);

  const { driveId: driveIdRouteParam } = useParams<{ driveId: string }>();
  const { publishedDriveUuid: publishedDriveUuidRouteParam } =
    useParams<{ publishedDriveUuid: string }>();

  /* 
    On viz page, the driveId comes from url
    when download is triggered from recording list, we will pass this driveId in as well as indicate whether it is public or not
  */
  const routeInfo = {
    routeDriveId: driveIdRouteParam
      ? parseInt(driveIdRouteParam, 10)
      : driveIdFromList,
    routePublishedDriveUUID: isPublic
      ? driveIdFromList
      : publishedDriveUuidRouteParam,
  };

  const { showToast } = useChakraHook();

  useEffect(() => {
    if (downloadState === DownloadState.Triggered) {
      // For ply files, we actually download a zip file containing the ply
      // and since we renamed the file, it causes some software can't open it because
      // the ply file doesn't have the same name as its content.
      // hence, we explicitly set the extension to zip for ply files
      const extension = name === 'ply' ? 'zip' : name;

      const saveFileName = getSafeFilename({
        recordingTitle,
        extension: String(extension),
        fileInfo,
      });
      /* 
        Here we pass the ideal file name to our backend so it can configure in
        'response-content-disposition' and file will be named correctly after download
      */
      const fetchOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          downloadFilename: saveFileName, // Send the desired filename
        }),
      };

      let DOWNLOAD_SIGNED_URL;

      if (driveOrgId) {
        DOWNLOAD_SIGNED_URL = `${BASE_ORG_API}/${driveOrgId}/recordings/${routeInfo.routeDriveId}/${type}/files/${fileInfo.id}/signed_url`;
      }

      if (routeInfo.routePublishedDriveUUID) {
        DOWNLOAD_SIGNED_URL = `${BASE_PUBLISHED_RECORDING_API}/${routeInfo.routePublishedDriveUUID}/${type}/files/${fileInfo.id}/signed_url`;
      } else if (!routeInfo.routeDriveId) {
        throw new Error('Cannot download file');
      }

      if (!DOWNLOAD_SIGNED_URL) return;

      fetchWithAuth(DOWNLOAD_SIGNED_URL, fetchOptions)
        .then((resp) => {
          if (!resp.ok) {
            throw new Error(
              `Something went wrong when creating signed URL for file ${fileInfo.id}`
            );
          }
          return resp.json();
        })
        .then((json) => {
          /* 
            If the download file is a tar file file, we need to check its content and extract the original file name. If the first level json file doesn't start with the file name, we need to add that as prefix.
          */

          if (fileInfo.filename.endsWith('.tar')) {
            const baseFileName = saveFileName.replace(/\.[^/.]+$/, '');

            if (fileInfo.filename.startsWith(baseFileName)) {
              // Tar file already has correct prefix in its name, use direct download
              downloadFileViaAnchor(json.url as string, saveFileName);
            } else {
              // Tar file needs processing (rename internal files)
              processAndDownloadTar(json.url as string, saveFileName).catch(
                (err) => {
                  console.error('Tar processing failed:', err);
                  // Fallback to regular download if tar processing fails
                  downloadFileViaAnchor(json.url as string, saveFileName);
                }
              );
            }
          } else {
            // the rename part is handled in backend. Then we can just create a download link
            // which gives us better support for download progress
            downloadFileViaAnchor(json.url as string, fileInfo.filename);
          }

          showToast({
            title: 'Processing file for download',
            description: `${saveFileName.replace(
              /\.ply$/,
              '.zip'
            )} will be downloaded shortly`,
            status: 'success',
          });
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }, [
    fileInfo,
    downloadState,
    routeInfo.routeDriveId,
    routeInfo.routePublishedDriveUUID,
    type,
    driveOrgId,
    recordingTitle,
    name,
    showToast,
  ]);

  // After a couple of seconds, assume the download has started and
  // update the state to allow users to download files again
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (downloadState === DownloadState.Triggered) {
      const timer = setTimeout(() => {
        setDownloadState(DownloadState.Started);
      }, DOWNLOAD_KICKOFF_TIME_MS);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [downloadState]);

  return (
    <DownloadOption
      // eslint-disable-next-line @typescript-eslint/no-misused-promises, @typescript-eslint/require-await
      onClick={async () => {
        // Already downloading
        if (downloadState === DownloadState.Triggered) {
          return;
        }
        setDownloadState(DownloadState.Triggered);
      }}
      disabled={downloadState === DownloadState.Triggered}
    >
      <div>
        <div>{name}</div>
        <SVGIcon size="16px" color="var(--color-crater-gray)">
          {downloadState === DownloadState.Ready ? <ArrowDownward /> : <Done />}
        </SVGIcon>
      </div>
      <div className="legal">{formatFileSizeAbbreviated(fileInfo.size)}</div>
    </DownloadOption>
  );
};
/* 
  The download content component will be reused in the list page's popover
  Export here for shared usage.
*/
export const DownloadContent: React.FC<{
  sourceDataForDownload: FileInfo[];
  sourceConfigForDownload?: FileInfo;
  downloadableOutputFiles: OutputFileInfo[];
  driveIdFromList?: string;
  isPublic?: boolean;
  driveOrgId?: number;
  recordingTitle?: string;
}> = ({
  sourceDataForDownload,
  sourceConfigForDownload,
  downloadableOutputFiles,
  driveIdFromList,
  isPublic,
  driveOrgId,
  recordingTitle,
}) => (
  <>
    {sourceConfigForDownload && (
      <Box display="grid" rowGap="9px" alignContent="start">
        <DownloadColumnTitle>Config</DownloadColumnTitle>
        <DownloadFile
          name={sourceConfigForDownload.filename.split('.').pop() ?? 'file'}
          fileInfo={sourceConfigForDownload}
          type="input"
          driveIdFromList={driveIdFromList}
          isPublic={isPublic}
          driveOrgId={driveOrgId}
          recordingTitle={recordingTitle}
        />
      </Box>
    )}

    {sourceDataForDownload.length > 0 && (
      <Box display="grid" rowGap="9px" alignContent="start">
        <DownloadColumnTitle>Data</DownloadColumnTitle>
        {sourceDataForDownload.map((file) => (
          <DownloadFile
            key={file.id}
            name={fileExtension(file.filename) ?? ''}
            fileInfo={file}
            type="input"
            driveIdFromList={driveIdFromList}
            isPublic={isPublic}
            driveOrgId={driveOrgId}
            recordingTitle={recordingTitle}
          />
        ))}
      </Box>
    )}
    {downloadableOutputFiles.length > 0 && (
      <Box display="grid" rowGap="9px" alignContent="start">
        <DownloadColumnTitle>
          {downloadableOutputFiles[0].output_type === 'slam'
            ? 'SLAM Output'
            : 'Output'}
        </DownloadColumnTitle>
        <DownloadGrid>
          {downloadableOutputFiles.map((file) => (
            <DownloadFile
              name={file.display_name ?? file.type}
              fileInfo={file}
              key={file.id}
              type="output"
              driveIdFromList={driveIdFromList}
              isPublic={isPublic}
              driveOrgId={driveOrgId}
              recordingTitle={recordingTitle}
            />
          ))}
        </DownloadGrid>
      </Box>
    )}
  </>
);

const DownloadMenu: React.FC<{
  revealed: boolean; // revealed is a prop so that the menu stays in the DOM and keeps track of downloaded state
  sourceDataForDownload: FileInfo[];
  sourceConfigForDownload?: FileInfo;
  downloadableOutputFiles: OutputFileInfo[];
  onClickedOutside: () => void;
  driveOrgId: number;
  recordingTitle?: string;
  buttonRef: React.RefObject<HTMLButtonElement>;
}> = ({
  revealed,
  sourceDataForDownload,
  sourceConfigForDownload,
  downloadableOutputFiles,
  onClickedOutside,
  driveOrgId,
  recordingTitle,
  buttonRef,
}) => {
  const downloadPopupRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, right: 0 });

  useOutsideCallback(downloadPopupRef, onClickedOutside);

  // Calculate position based on button position
  useEffect(() => {
    if (revealed && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || document.documentElement.scrollTop;

      setPosition({
        top: buttonRect.bottom + scrollY + 8,
        right: window.innerWidth - buttonRect.right,
      });
    }
  }, [revealed, buttonRef]);

  const hasFiles =
    sourceConfigForDownload ||
    sourceDataForDownload.length > 0 ||
    downloadableOutputFiles.length > 0;

  return (
    <DownloadPopup
      revealed={revealed}
      className="legal"
      ref={downloadPopupRef}
      style={{
        top: position.top,
        right: position.right,
      }}
    >
      {hasFiles ? (
        <DownloadContent
          sourceDataForDownload={sourceDataForDownload}
          sourceConfigForDownload={sourceConfigForDownload}
          downloadableOutputFiles={downloadableOutputFiles}
          driveOrgId={driveOrgId}
          recordingTitle={recordingTitle}
        />
      ) : (
        <div>No files available for download</div>
      )}
    </DownloadPopup>
  );
};

/// ///////////////////////////////////////////////////////////////////////////
// Final NavBar
/// ///////////////////////////////////////////////////////////////////////////

const DriveNavBar: React.FC<{ orgId: number; hasRGBChannels: boolean }> = ({
  orgId,
  hasRGBChannels = true, // default to true to avoid publish option is enabled before we receive data
}) => {
  const { isPublished, driveInfo, isShared, publicationInfo } = useStoreState(
    (store) => ({
      ...store.drive,
    })
  );
  const { curUserOrg, appUser, isOusterUser } = useStoreState((store) => ({
    ...store.user,
  }));
  const { deleteDrive, shareWithOuster, loadNewContentStatus } =
    useStoreActions((actions) => actions.drive);
  const { openConfirmDialog } = useStoreActions(
    (storeState) => storeState.confirmDialog
  );
  const { shortcuts } = useStoreState((store) => ({
    ...store.shortcut,
  }));

  const isSampleRecording =
    driveInfo?.accessLevel?.includes('_SAMPLE') ?? false;
  const hasKcToken = Boolean(localStorage.getItem('kctoken'));

  const queryClient = useQueryClient();

  const {
    sourceFileInfo,
    outputForDownload,
    outputForDownloadNonSlam,
    sourceDataForDownload,
    sourceConfigForDownload,
  } = useStoreState((store) => ({ ...store.drive }));
  const { addShortcut } = useStoreActions((actions) => actions.shortcut);

  const [sharingRevealed, setSharingRevealed] = useState(false);
  const [downloadRevealed, setDownloadRevealed] = useState(false);
  const [shortcutsRevealed, setShortcutsRevealed] = useState(false);

  const {
    isOpen: isShareModalOpen,
    onOpen: onShareModalOpen,
    onClose: onShareModalClose,
  } = useDisclosure();

  const {
    isOpen: isUnshareAlertOpen,
    onOpen: onUnshareAlertOpen,
    onClose: onUnshareAlertClose,
  } = useDisclosure();

  const {
    isOpen: isDownloadWarningModalOpen,
    onOpen: onDownloadWarningModalOpen,
    onClose: onDownloadWarningModalClose,
  } = useDisclosure();

  const { showToast } = useChakraHook();
  const navigate = useNavigate();
  const { publishedDriveUuid: publishedDriveUuidRouteParam } =
    useParams<{ publishedDriveUuid: string }>();

  useEffect(() => {
    addShortcut(
      new Shortcut(
        ShortcutCategory.Other,
        [
          // I think this is more usable but we should see how it works on
          // international keyboards like AZERTY
          new ShortcutKey('?', [ShortcutAddonKey.SHIFT], '/'),
          new ShortcutKey('/', [ShortcutAddonKey.CTRL]),
        ],
        () => {
          setShortcutsRevealed((wasRevealed) => !wasRevealed);
        },
        'Toggle Shortcuts'
      )
    );
  }, [addShortcut]);

  /* 
    Control for recording publicity configuration modal
  */
  const {
    isOpen: isPublicitySettingOpen,
    onOpen: onPublicitySettingOpen,
    onClose: onPublicitySettingClose,
  } = useDisclosure();
  const publicityRecordingRef = useRef<
    RecordingPublicityAccessConfig | undefined
  >(undefined);

  const onConfigureSamples = () => {
    if (!driveInfo) return;
    publicityRecordingRef.current = {
      organization_id: driveInfo.organizationId,
      id: Number(driveInfo.id),
      publication_uuid: publicationInfo?.uuid,
      data_category: driveInfo.dataCategory,
      sensor_info: driveSensorInfoArrayToRaw(driveInfo.sensorInfos),
      start_time: driveInfo.startTime === '...' ? null : driveInfo.startTime,
      recording_title: driveInfo.recordingTitle,
    };

    onPublicitySettingOpen();
  };

  const onPublicitySettingCloseHandler = useCallback(() => {
    publicityRecordingRef.current = undefined;
    if (localStorage.getItem('recordingAccessConfigChanged')) {
      queryClient.invalidateQueries({ queryKey: ['shared-from-list'] });
      queryClient.invalidateQueries({ queryKey: ['drive-list'] });

      localStorage.removeItem('recordingAccessConfigChanged');
    }

    onPublicitySettingClose();
  }, [onPublicitySettingClose, queryClient]);

  const onDeleteDrive = () => {
    if (!driveInfo) return;

    const onConfirm = async () => {
      try {
        await deleteDrive({ orgId, driveId: driveInfo.id });

        if (isShared) {
          // If the drive is shared, we need to invalidate the shared list
          // to remove the deleted drive from the list
          queryClient.invalidateQueries({ queryKey: ['shared-with-list'] });
        }
        // when an ouster published drive is deleted. invalidate sample data query
        if (
          driveInfo.accessLevel !== AccessLevel.NONE &&
          curUserOrg?.id === 1
        ) {
          queryClient.invalidateQueries({
            queryKey: ['public-public-drive-list'],
          });
          queryClient.invalidateQueries({
            queryKey: ['private-public-drive-list'],
          });

          loadNewContentStatus({ organizationId: curUserOrg.id });
        }

        showToast({
          title: 'Recording deleted',
          description: `Recording deleted, redirecting to recordings page in 3 seconds`,
          status: 'info',
        });
        setTimeout(() => {
          navigate('/drive/private');
        }, 2000);
      } catch (error) {
        showToast({
          title: 'Failed to delete recording',
          description: `${error}`,
          status: 'error',
        });
      }
    };

    // Build bullets at call site so we can include context-specific warnings
    const bullets = [
      'This recording will be permanently deleted and cannot be recovered.',
    ];
    if (isPublished) {
      bullets.push(
        'This recording is published and will be removed from public listings.'
      );
    }
    if (isShared) {
      bullets.push(
        'This recording is shared with Ouster. Deleting it will remove access for the Ouster support team.'
      );
    }

    openConfirmDialog({
      title: 'Delete recording',
      body: 'Are you sure you want to delete this recording?',
      bullets,
      onConfirm,
      isDestructive: true,
      confirmLabel: 'Delete recording',
      cancelLabel: 'Cancel',
    });
  };

  const feedbackFormLink = useFeedbackForm();
  const onOpenFeedbackForm = () => {
    window.open(feedbackFormLink, '_blank');
  };

  const handleDownloadRequest = () => {
    if (hasKcToken || !isSampleRecording) {
      setDownloadRevealed(true);
    } else {
      onDownloadWarningModalOpen();
    }
  };

  const getMenuItems = (): TMenuItem[] => {
    const items: TMenuItem[] = [];

    if (!hasRGBChannels || isOusterUser) {
      items.push({
        label: 'Download Recording',
        action: 'DOWNLOAD-RECORDING',
        icon: <Icon icon="download" />,
      });
    }
    items.push({
      label: 'Copy the visualization link',
      action: 'COPY-RECORDING-LINK',
      icon: <Icon icon="share" />,
    });

    // Items available only if the drive belongs to the current user's organization
    if (driveInfo?.organizationId === curUserOrg?.id) {
      // Ouster user don't need to share button
      if (curUserOrg?.id !== 1) {
        items.push({
          label: isShared
            ? 'Stop Sharing with Ouster'
            : 'Share Recording with Ouster',
          action: isShared ? 'UNSHARE-RECORDING' : 'SHARE-RECORDING',
          icon: <Icon icon={isShared ? 'cancelSend' : 'send'} />,
        });
      }
      // Items available if the user is the uploader or an admin
      if (
        appUser?.id === driveInfo?.uploadedById ||
        curUserOrg?.role === 'ADMIN'
      ) {
        items.push({
          label: 'Delete Recording',
          action: 'DELETE-RECORDING',
          icon: <Icon icon="delete" />,
        });
      }

      if (curUserOrg?.id === 1 && curUserOrg?.role === 'ADMIN') {
        // Ouster user can configure sample data
        items.push({
          label: 'Configure Sample Access',
          action: 'SAMPLE-CONFIGURE',
          icon: <TbEyeStar size={24} />,
        });
      }
    }

    // Publish/unpublish option for any logged-in user and user can only do that within their own organization
    if (
      curUserOrg &&
      curUserOrg.id === driveInfo?.organizationId &&
      !hasRGBChannels
    ) {
      if (!isPublished) {
        items.push({
          label: 'Publish Recording',
          action: 'PUBLISH-RECORDING',
          icon: <Icon icon="public" />,
        });
      }
      // only the admin or the one who published can unpublish it
      if (
        isPublished &&
        (appUser?.id === publicationInfo?.publishedById ||
          curUserOrg?.role === 'ADMIN')
      ) {
        items.push({
          label: 'Manage Published Recording',
          action: 'UNPUBLISH-RECORDING',
          icon: <Icon icon="cloudSync" />,
        });
      }
    }

    // Additional common actions
    items.push(
      {
        label: 'Send Feedback',
        action: 'SEND-FEEDBACK',
        icon: <Icon icon="feedback" />,
      },
      {
        label: 'Keyboard Shortcuts',
        action: 'KEYBOARD-SHORTCUTS',
        icon: <Icon icon="keyboardAlt" />,
      }
    );

    return items;
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast({
        title: '',
        description: 'Link copied to clipboard',
        status: 'success',
      });
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const copyCloudVizLink = () => {
    const isPublic = publishedDriveUuidRouteParam;
    const fullUrl = isPublic
      ? `${window.location.origin}/share/${publishedDriveUuidRouteParam}`
      : `${window.location.origin}/organization/${driveInfo?.organizationId}/drive/${driveInfo?.id}`;
    copyToClipboard(fullUrl);
  };

  const handleMenuClick = (item: TMenuItem) => {
    switch (item.action) {
      case 'DOWNLOAD-RECORDING':
        handleDownloadRequest();
        break;
      case 'SHARE-RECORDING':
        onShareModalOpen();
        break;
      case 'UNSHARE-RECORDING':
        onUnshareAlertOpen();
        break;
      case 'DELETE-RECORDING':
        onDeleteDrive();
        break;
      case 'PUBLISH-RECORDING':
      case 'UNPUBLISH-RECORDING':
        setSharingRevealed(true);
        break;
      case 'COPY-RECORDING-LINK':
        copyCloudVizLink();
        break;
      case 'SAMPLE-CONFIGURE':
        onConfigureSamples();
        break;
      case 'SEND-FEEDBACK':
        onOpenFeedbackForm();
        break;
      case 'KEYBOARD-SHORTCUTS':
        setShortcutsRevealed(true);
        break;
      default:
        console.warn(`Unsupported action: ${item.action}`);
        break;
    }
  };

  const {
    isOpen: isMenuOpen,
    onClose: onMenuClose,
    onOpen: onMenuOpen,
  } = useDisclosure();

  const onShareRecording = async (notes?: string) => {
    if (!curUserOrg?.id || !driveInfo?.id) return;
    try {
      await shareWithOuster({
        organizationId: curUserOrg.id,
        driveId: driveInfo?.id,
        shareWithOuster: true,
        notes,
      });
      showToast({
        title: 'Recording shared',
        description: `Recording has been successfully shared with Ouster`,
        status: 'info',
      });
      queryClient.invalidateQueries({ queryKey: ['shared-with-list'] });
    } catch (error) {
      showToast({
        title: 'Failed to share recording',
        description: `${error}`,
        status: 'error',
      });
    }
  };

  const onUnshareConfirm = () => {
    const unshare = async () => {
      if (!curUserOrg?.id || !driveInfo?.id) return;

      try {
        await shareWithOuster({
          organizationId: curUserOrg.id,
          driveId: driveInfo?.id,
          shareWithOuster: false,
        });
        onUnshareAlertClose();
        showToast({
          title: 'Recording unshared',
          description: `Recording has been unshared with Ouster`,
          status: 'info',
        });
        queryClient.invalidateQueries({ queryKey: ['shared-with-list'] });
      } catch (error) {
        showToast({
          title: 'Failed to unshared recording',
          description: `${error}`,
          status: 'error',
        });
      }
    };
    unshare();
  };

  const downloadButtonRef = useRef<HTMLButtonElement>(null);

  const vizTitleProps: {
    text?: string;
    recordingTitle?: string;
    details?: string;
  } = {};
  if (driveInfo) {
    const sensorInfos = driveInfo.sensorInfos || [];
    const sensorInfo = sensorInfos[0];
    const isMultisensor = sensorInfos.length > 1;
    const details: string[] = [];
    if (isMultisensor) {
      details.push('Multi-sensor');
    } else if (sensorInfo) {
      details.push(
        `${sensorInfo.sdkProductType} - ${sensorInfo.sdkProductResolution} | ${sensorInfo.sdkFwVersionCore} | Rev ${sensorInfo.sdkProductHwRevision} | ${sensorInfo.sdkLidarMode}`
      );
    }
    if (driveInfo.uploadedBy) {
      details.push(`by ${driveInfo.uploadedBy}`);
    }
    if (isShared) {
      details.push('Shared with Ouster');
    }
    const recTitle = driveInfo.recordingTitle || undefined;
    const detailsText = details.filter(Boolean).join(' | ');
    if (!recTitle) {
      const titleSegments: string[] = [];
      if (isMultisensor) {
        titleSegments.push('Multi-sensor');
      } else if (sensorInfo) {
        titleSegments.push(
          `${sensorInfo.sdkProductType} - ${sensorInfo.sdkProductResolution} | ${sensorInfo.sdkFwVersionCore} | Rev ${sensorInfo.sdkProductHwRevision} | ${sensorInfo.sdkLidarMode}`
        );
      }
      if (driveInfo.uploadedBy) {
        titleSegments.push(`by ${driveInfo.uploadedBy}`);
      }
      if (isShared) {
        titleSegments.push('Shared with Ouster');
      }
      const titleText = titleSegments.filter(Boolean).join(' | ');
      vizTitleProps.text = titleText;
    } else {
      vizTitleProps.recordingTitle = recTitle;
      vizTitleProps.details = detailsText;
    }
  }

  return (
    <>
      <NavBar
        data-testid="drive-nav-bar-root"
        centerElement={
          driveInfo && (
            <NavFullHeightContainer
              style={{
                overflow: 'hidden',
                placeItems: 'unset',
                alignItems: 'center',
              }}
            >
              {'recordingTitle' in vizTitleProps ? (
                <VizTitle
                  recordingTitle={vizTitleProps.recordingTitle}
                  details={vizTitleProps.details}
                />
              ) : (
                <VizTitle text={vizTitleProps.text} />
              )}
            </NavFullHeightContainer>
          )
        }
        rightElement={
          <NavFullHeightContainer>
            <NavFlexRow>
              {(!hasRGBChannels || isOusterUser) && (
                <IconButton
                  ref={downloadButtonRef}
                  emphasis="subtle"
                  variant="solid"
                  icon="download"
                  onClick={handleDownloadRequest}
                  data-testid="drive-nav-bar-download-btn"
                />
              )}

              <Menu
                placement="top-end"
                offset={[5, 10]}
                autoSelect={false}
                onClose={onMenuClose}
                isOpen={isMenuOpen}
                data-testid="drive-nav-bar-settings-menu"
              >
                <MenuButton
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  as={IconButton}
                  emphasis="subtle"
                  icon="settings"
                  color="white"
                  onClick={onMenuOpen}
                  data-testid="drive-nav-bar-settings-btn"
                />
                <MenuList data-testid="drive-nav-bar-menu-list">
                  {getMenuItems().map((item) => (
                    <MenuItem
                      key={item.action}
                      onClick={() => handleMenuClick(item)}
                      style={{
                        fontSize: 14,
                        paddingBlock: '0.75rem',
                        paddingInline: '1.25rem',
                        marginBlock: '0.25rem',
                        columnGap: '0.5rem',
                      }}
                      _hover={{ bgColor: 'var(--color-blast-off-blue)' }}
                      _focus={{ bgColor: 'var(--color-blast-off-blue)' }}
                      data-testid={`drive-nav-bar-menu-item-${item.action.toLowerCase()}`}
                    >
                      <span style={{ fontSize: 20 }}>{item.icon}</span>
                      {item.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>

              {curUserOrg?.id && <NavUser data-testid="drive-nav-bar-user" />}
            </NavFlexRow>
          </NavFullHeightContainer>
        }
      />
      <DownloadMenu
        revealed={downloadRevealed}
        sourceDataForDownload={sourceDataForDownload}
        sourceConfigForDownload={sourceConfigForDownload}
        downloadableOutputFiles={
          outputForDownload.length > 0
            ? outputForDownload
            : outputForDownloadNonSlam
        }
        driveOrgId={orgId}
        buttonRef={downloadButtonRef}
        onClickedOutside={() => {
          if (downloadRevealed) {
            setDownloadRevealed(false);
          }
        }}
        recordingTitle={driveInfo?.recordingTitle}
        data-testid="drive-nav-bar-download-menu"
      />
      <NavPublish
        revealed={sharingRevealed}
        sourceFilesInfo={sourceFileInfo}
        onClickedOutside={() => {
          if (sharingRevealed) {
            setSharingRevealed(false);
          }
        }}
      />
      <ShortcutsModal
        isOpen={shortcutsRevealed}
        onClose={() => {
          if (shortcutsRevealed) {
            setShortcutsRevealed(false);
          }
        }}
        isMac={isMacOs}
        shortcuts={shortcuts}
      />
      {isShareModalOpen && (
        <ShareWithOusterModal
          isOpen={isShareModalOpen}
          handleClose={onShareModalClose}
          handleShare={onShareRecording}
        />
      )}

      <UnshareWithOusterAlert
        isUnshareAlertOpen={isUnshareAlertOpen}
        onUnshareAlertClose={onUnshareAlertClose}
        onUnshareConfirm={onUnshareConfirm}
      />

      <ModalContainer
        isOpen={isDownloadWarningModalOpen}
        onClose={onDownloadWarningModalClose}
        header="Unlock Sample lidar Downloads"
        footer={null}
        content={<PublicDownloadWarning />}
        data-testid="public-collection-download-warning-modal"
      />

      {publicityRecordingRef.current && (
        <ModalContainer
          isOpen={isPublicitySettingOpen}
          onClose={onPublicitySettingCloseHandler}
          header="Recording Access Configuration"
          footer={null}
          content={
            <RecordingAccessConfig
              recording={publicityRecordingRef.current}
              onClose={onPublicitySettingCloseHandler}
            />
          }
        />
      )}
    </>
  );
};

export { DriveNavBar };
